import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageIcon, Video, Bot, BookOpen, FileText, Music, ArrowRight } from 'lucide-react';
import {
  TaskyDialog,
  TaskyButton,
  TaskyBadge,
} from '../tasky';
import { RecentWorkItem, RecentWorkType } from '../../services/api/recentWorksService';

const typeLabelMap: Record<RecentWorkType, string> = {
  chat: 'AIチャット',
  image: '画像生成',
  video: '動画広告',
  manga: '漫画広告',
  lp: 'LP制作',
  music: '音楽生成',
};

const typeIconMap: Record<RecentWorkType, React.ReactNode> = {
  chat: <Bot className="w-5 h-5" />,
  image: <ImageIcon className="w-5 h-5" />,
  video: <Video className="w-5 h-5" />,
  manga: <BookOpen className="w-5 h-5" />,
  lp: <FileText className="w-5 h-5" />,
  music: <Music className="w-5 h-5" />,
};

const typeToneMap: Record<RecentWorkType, { bg: string; color: string }> = {
  chat: { bg: 'bg-primary/10', color: 'text-primary' },
  image: { bg: 'bg-primary/10', color: 'text-primary' },
  video: { bg: 'bg-purple/10', color: 'text-purple' },
  manga: { bg: 'bg-success/10', color: 'text-success' },
  lp: { bg: 'bg-success/10', color: 'text-success' },
  music: { bg: 'bg-purple/10', color: 'text-purple' },
};

const statusLabelMap: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'info' | 'default' }> = {
  pending: { label: '処理待ち', variant: 'warning' },
  processing: { label: '処理中', variant: 'info' },
  generating: { label: '生成中', variant: 'info' },
  generating_audio: { label: '音声生成中', variant: 'info' },
  generating_jacket: { label: 'ジャケット生成中', variant: 'info' },
  wireframe_generating: { label: 'ワイヤーフレーム生成中', variant: 'info' },
  wireframe_done: { label: 'ワイヤーフレーム完成', variant: 'info' },
  design_generating: { label: 'デザイン生成中', variant: 'info' },
  design_done: { label: 'デザイン完成', variant: 'info' },
  in_progress: { label: '進行中', variant: 'info' },
  completed: { label: '完了', variant: 'success' },
  done: { label: '完了', variant: 'success' },
  draft: { label: '下書き', variant: 'default' },
  failed: { label: '失敗', variant: 'destructive' },
  error: { label: 'エラー', variant: 'destructive' },
};

const formatFullDateTime = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}/${mo}/${day} ${h}:${mi}`;
};

const formatRelativeTime = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'たった今';
  if (m < 60) return `${m}分前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}時間前`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}日前`;
  return new Date(iso).toLocaleDateString('ja-JP');
};

export interface RecentWorkDetailModalProps {
  item: RecentWorkItem | null;
  onClose: () => void;
}

export const RecentWorkDetailModal: React.FC<RecentWorkDetailModalProps> = ({ item, onClose }) => {
  const navigate = useNavigate();

  if (!item) {
    return null;
  }

  const typeLabel = typeLabelMap[item.type] || '作業';
  const typeIcon = typeIconMap[item.type] || <Bot className="w-5 h-5" />;
  const tone = typeToneMap[item.type] || { bg: 'bg-gray-100', color: 'text-gray-600' };
  const statusInfo = item.status ? statusLabelMap[item.status] : null;

  const handleOpen = () => {
    navigate(item.deep_link);
    onClose();
  };

  return (
    <TaskyDialog open onClose={onClose} title="作業の詳細" size="lg">
      <div className="space-y-5">
        {/* ヘッダー: 種別アイコン + ラベル */}
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-tasky-md ${tone.bg} flex items-center justify-center ${tone.color}`}>
            {typeIcon}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">種別</p>
            <p className="text-base font-bold text-dark">{typeLabel}</p>
          </div>
        </div>

        {/* サムネイル（あれば） */}
        {item.thumbnail_url && (
          <div className="w-full max-h-72 bg-warm-surface rounded-tasky-sm overflow-hidden flex items-center justify-center border border-warm-border">
            <img
              src={item.thumbnail_url}
              alt={item.title}
              className="max-w-full max-h-72 object-contain"
            />
          </div>
        )}

        {/* タイトル / プロンプト */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
            {item.type === 'chat' ? '最初のメッセージ' : 'プロンプト / タイトル'}
          </p>
          <p className="text-sm text-dark whitespace-pre-wrap leading-relaxed break-words">
            {item.title || <em className="text-gray-400">（タイトルなし）</em>}
          </p>
        </div>

        {/* サブ情報 */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">内容</p>
          <p className="text-sm text-gray-600">{item.subtitle}</p>
        </div>

        {/* ステータス + 更新日時 */}
        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-warm-border">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">ステータス</p>
            {statusInfo ? (
              <TaskyBadge variant={statusInfo.variant}>{statusInfo.label}</TaskyBadge>
            ) : (
              <span className="text-sm text-gray-500">—</span>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">更新日時</p>
            <p className="text-sm text-dark">
              {formatFullDateTime(item.updated_at)}
              <span className="ml-2 text-xs text-gray-400">（{formatRelativeTime(item.updated_at)}）</span>
            </p>
          </div>
        </div>

        {/* アクション */}
        <div className="flex justify-end gap-2 pt-4 border-t border-warm-border">
          <TaskyButton variant="outline" onClick={onClose}>
            閉じる
          </TaskyButton>
          <TaskyButton onClick={handleOpen}>
            このプロジェクトを開く
            <ArrowRight className="w-4 h-4 ml-1" />
          </TaskyButton>
        </div>
      </div>
    </TaskyDialog>
  );
};

export default RecentWorkDetailModal;
